import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HotelLayout from '@/components/Hotels/HotelLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, Search, MapPin, Hotel, DollarSign, Users, Star, Clock, Shield, ArrowRight } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { POPULAR_DESTINATIONS, MOCK_HOTELS, MOCK_PRICES } from '@/utils/hotels/mockData';

const HotelsHomepage: React.FC = () => {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState<Date | undefined>(new Date());
  const [checkOut, setCheckOut] = useState<Date | undefined>(addDays(new Date(), 3));
  
  // Get featured hotels (top rated)
  const featuredHotels = [...MOCK_HOTELS]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 3);
  
  // Get best deals based on discount percentage
  const bestDeals = MOCK_PRICES
    .filter(price => price.discount && price.discount > 10)
    .sort((a, b) => (b.discount || 0) - (a.discount || 0))
    .slice(0, 4);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/hotels/search');
  };

  return (
    <HotelLayout transparentHeader>
      {/* Hero section */}
      <div className="relative h-[600px] md:h-[700px] lg:h-[800px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70">
          <img 
            src="https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2070&auto=format&fit=crop" 
            alt="Luxury hotel" 
            className="absolute w-full h-full object-cover -z-10" 
          />
        </div>

        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 font-chroma">Find Your Perfect Stay</h1>
          <p className="text-lg md:text-xl text-white mb-8 max-w-2xl">Compare prices from hundreds of booking sites to find the best hotel deals worldwide.</p>
          
          {/* Search form */}
          <Card className="w-full max-w-5xl bg-background/95 backdrop-blur rounded-xl shadow-lg">
            <CardContent className="p-6">
              <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Destination</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input 
                      placeholder="Where are you going?" 
                      className="pl-9" 
                      defaultValue="New York"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Check-in / Check-out</label>
                  <div className="flex space-x-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {checkIn ? format(checkIn, 'MMM dd') : <span>Check-in</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={checkIn}
                          onSelect={setCheckIn}
                          initialFocus
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                    
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {checkOut ? format(checkOut, 'MMM dd') : <span>Check-out</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={checkOut}
                          onSelect={setCheckOut}
                          initialFocus
                          disabled={(date) => date <= (checkIn || new Date())}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Guests & Rooms</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Select defaultValue="2-1">
                      <SelectTrigger className="pl-9">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-1">1 Adult, 1 Room</SelectItem>
                        <SelectItem value="2-1">2 Adults, 1 Room</SelectItem>
                        <SelectItem value="2-2">2 Adults, 2 Rooms</SelectItem>
                        <SelectItem value="4-2">4 Adults, 2 Rooms</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button type="submit" className="h-10 mt-auto bg-primary hover:bg-primary/90">
                  <Search className="h-4 w-4 mr-2" /> Find Hotels
                </Button>
              </form>
            </CardContent>
          </Card>
          
          <div className="mt-6 text-white/90">
            <p className="text-sm">Popular: New York, Miami, San Francisco, Chicago, Boston</p>
          </div>
        </div>
      </div>

      {/* Popular destinations */}
      <div className="container mx-auto px-4 py-16">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold font-chroma">Popular Destinations</h2>
          <Button variant="ghost" className="hidden md:flex items-center">
            View All <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
          {POPULAR_DESTINATIONS.map((destination, index) => (
            <div 
              key={index} 
              className="relative h-60 rounded-xl overflow-hidden group cursor-pointer hover-scale"
              onClick={() => navigate('/hotels/search')}
            >
              <img 
                src={destination.image} 
                alt={destination.name} 
                className="absolute w-full h-full object-cover z-0 group-hover:scale-110 transition-transform duration-500" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 group-hover:from-black/80 transition-all duration-300 z-10">
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
                <h3 className="text-2xl font-bold text-white font-chroma">{destination.name}</h3>
                <p className="text-white/90">{destination.country}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Today's best deals */}
      <div className="bg-muted py-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold font-chroma">Today's Best Deals</h2>
            <Button variant="ghost" className="hidden md:flex items-center" onClick={() => navigate('/hotels/search')}>
              View All Deals <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {bestDeals.map((deal, index) => {
              const hotel = MOCK_HOTELS.find(h => h.id === deal.hotelId);
              if (!hotel) return null;
              
              return (
                <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow hover-scale">
                  <div className="relative h-40">
                    <img 
                      src={hotel.thumbnail} 
                      alt={hotel.name} 
                      className="w-full h-full object-cover"
                    />
                    <Badge className="absolute top-2 right-2 bg-red-500 hover:bg-red-600">
                      Save {deal.discount}%
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg line-clamp-1 font-chroma">{hotel.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{hotel.city}, {hotel.country}</p>
                    <div className="flex items-center mb-2">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      <span className="ml-1 text-sm">{hotel.rating.toFixed(1)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-sm line-through text-muted-foreground">${deal.originalPrice}</span>
                        <span className="text-lg font-bold">${deal.price}</span>
                      </div>
                      <Button size="sm" onClick={() => navigate(`/hotels/${hotel.id}`)}>
                        View Deal
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Featured hotels */}
      <div className="container mx-auto px-4 py-16">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold font-chroma">Featured Hotels</h2>
          <Button variant="ghost" className="hidden md:flex items-center">
            View All <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {featuredHotels.map((hotel) => {
            const lowestPrice = MOCK_PRICES
              .filter(price => price.hotelId === hotel.id)
              .sort((a, b) => a.price - b.price)[0];
              
            return (
              <Card key={hotel.id} className="overflow-hidden hover:shadow-lg transition-shadow hover-scale">
                <div className="relative h-48">
                  <img 
                    src={hotel.thumbnail} 
                    alt={hotel.name} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm px-2 py-1 rounded text-sm font-medium">
                    {hotel.stars} ★
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg font-chroma">{hotel.name}</h3>
                  <div className="flex items-center mb-2">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="ml-1 text-sm text-muted-foreground">{hotel.location}, {hotel.city}</span>
                  </div>
                  <div className="flex items-center mb-3">
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    <span className="ml-1 text-sm">{hotel.rating.toFixed(1)} ({hotel.reviewCount} reviews)</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {hotel.amenities.slice(0, 3).map((amenity, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {amenity}
                      </Badge>
                    ))}
                    {hotel.amenities.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{hotel.amenities.length - 3} more
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground">From</span>
                      <span className="text-lg font-bold font-chroma">${lowestPrice ? lowestPrice.price : '---'}</span>
                    </div>
                    <Button onClick={() => navigate(`/hotels/${hotel.id}`)}>
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Why choose us */}
      <div className="bg-muted py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center font-chroma">Why Book With HotelFinder</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="flex flex-col items-center text-center p-4">
              <DollarSign size={48} className="text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2 font-chroma">Best Price Guarantee</h3>
              <p className="text-foreground/70">Find a lower price? We'll refund you the difference.</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <Hotel size={48} className="text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2 font-chroma">Over 2 Million Hotels</h3>
              <p className="text-foreground/70">Discover your perfect stay from our vast selection of properties.</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <Clock size={48} className="text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2 font-chroma">24/7 Customer Service</h3>
              <p className="text-foreground/70">Our support team is always ready to assist you with your booking.</p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <Shield size={48} className="text-primary mb-4" />
              <h3 className="text-xl font-bold mb-2 font-chroma">Secure Booking</h3>
              <p className="text-foreground/70">Your payment and personal information are always protected.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Testimonials */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center font-chroma">What Our Customers Say</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="border-border/40">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-foreground/80 mb-4">
                "I saved over $200 on my stay in New York by using HotelFinder. The comparison feature is amazing and helped me find the best deal quickly."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">JD</div>
                <div className="ml-3">
                  <p className="font-medium">John D.</p>
                  <p className="text-sm text-muted-foreground">New York</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-border/40">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-foreground/80 mb-4">
                "The interface is so easy to use. I was able to filter by my preferences and find exactly what I was looking for. Highly recommend!"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">SM</div>
                <div className="ml-3">
                  <p className="font-medium">Sarah M.</p>
                  <p className="text-sm text-muted-foreground">Chicago</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-border/40">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-foreground/80 mb-4">
                "The best thing about HotelFinder is that it shows you all the booking options in one place. No need to check multiple websites."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">RL</div>
                <div className="ml-3">
                  <p className="font-medium">Robert L.</p>
                  <p className="text-sm text-muted-foreground">Miami</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* App download section */}
      <div className="bg-primary text-primary-foreground py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="md:w-1/2">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 font-chroma">Download Our Mobile App</h2>
              <p className="mb-6 text-primary-foreground/90">Get exclusive app-only deals and manage your bookings on the go.</p>
              <div className="flex flex-wrap gap-4">
                <Button variant="secondary" className="h-12">
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg">
                    <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                  </svg>
                  App Store
                </Button>
                <Button variant="secondary" className="h-12">
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                    <path fill="currentColor" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                  </svg>
                  Google Play
                </Button>
              </div>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <img 
                src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=2070&auto=format&fit=crop"
                alt="Mobile app" 
                className="max-h-[300px] rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Newsletter */}
      <div className="container mx-auto px-4 py-16">
        <div className="bg-muted rounded-xl p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 font-chroma">Subscribe to Our Newsletter</h2>
          <p className="mb-6 max-w-xl mx-auto">Get the latest deals and travel inspiration delivered straight to your inbox.</p>
          <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <Input placeholder="Your email address" className="flex-1" />
            <Button>Subscribe</Button>
          </div>
        </div>
      </div>
    </HotelLayout>
  );
};

export default HotelsHomepage;
